export type LeadTimeInputs = {
  /** Null when the buyer has not entered a stock level. */
  currentStock: number | null;
  customsDays: number;
  /** Null when the buyer has not entered a demand rate. */
  dailyDemand: number | null;
  productionDays: number;
  safetyBufferDays: number;
  transitDays: number;
};

export type LeadTimeStatus = 'healthy' | 'warning' | 'critical';

export type LeadTimeResult = {
  coverageWeeksAtReorder: number | null;
  daysUntilStockout: number | null;
  /** Production + transit + customs. The buffer is deliberately excluded. */
  leadTimeDays: number;
  /** Signed days until the order must be placed. Negative means overdue. */
  orderByDays: number | null;
  /** ISO (YYYY-MM-DD) date the order must be placed by, past dates included. */
  orderByDate: string | null;
  /** Lead time plus the safety buffer — the horizon a reorder must cover. */
  planningHorizonDays: number;
  reorderPoint: number | null;
  safetyStock: number | null;
  status: LeadTimeStatus | null;
  statusMessage: string | null;
  warnings: string[];
};

export const LEAD_TIME_ASSUMPTIONS = {
  /** Safety stock is modelled as days of cover, not a service-level formula. */
  safetyStockMethod: 'days of demand covered by the safety buffer',
  daysPerWeek: 7,
} as const;

function asNonNegative(value: number) {
  return Number.isFinite(value) ? Math.max(0, value) : 0;
}

export function parseNumericInput(value: string): number {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
}

/** Empty string means "not provided", which is different from zero. */
export function parseOptionalInput(value: string): number | null {
  if (value.trim().length === 0) {
    return null;
  }

  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? Math.max(0, parsed) : null;
}

/**
 * Returns the reason the lead time cannot be modelled, or null when it can.
 */
export function getLeadTimeInputError(input: {
  customsDays: number;
  productionDays: number;
  transitDays: number;
}): string | null {
  const total =
    asNonNegative(input.productionDays) +
    asNonNegative(input.transitDays) +
    asNonNegative(input.customsDays);

  if (total <= 0) {
    return 'Enter production, transit, or customs days to calculate a lead time.';
  }

  return null;
}

function toIsoDate(today: Date, offsetDays: number) {
  const date = new Date(today.getTime());
  date.setDate(date.getDate() + offsetDays);

  return date.toISOString().split('T')[0]!;
}

export function calculateLeadTime(
  input: LeadTimeInputs,
  today: Date = new Date(),
): LeadTimeResult {
  const productionDays = asNonNegative(input.productionDays);
  const transitDays = asNonNegative(input.transitDays);
  const customsDays = asNonNegative(input.customsDays);
  const safetyBufferDays = asNonNegative(input.safetyBufferDays);

  const dailyDemand =
    input.dailyDemand === null ? null : asNonNegative(input.dailyDemand);
  const currentStock =
    input.currentStock === null ? null : asNonNegative(input.currentStock);

  // Lead time is what the supply chain actually takes. The safety buffer is a
  // planning cushion on top of it, so the two are reported separately.
  const leadTimeDays = productionDays + transitDays + customsDays;
  const planningHorizonDays = leadTimeDays + safetyBufferDays;

  const warnings: string[] = [];

  if (safetyBufferDays === 0) {
    warnings.push(
      'With no safety buffer, the reorder point assumes every supplier and shipping estimate lands exactly on time.',
    );
  }

  if (transitDays === 0) {
    warnings.push(
      'No transit time entered. Ocean freight from Asia is usually 20 to 40 days door to door.',
    );
  }

  const base = {
    leadTimeDays,
    planningHorizonDays,
    warnings,
  };

  if (dailyDemand === null || dailyDemand <= 0) {
    return {
      ...base,
      coverageWeeksAtReorder: null,
      daysUntilStockout: null,
      orderByDate: null,
      orderByDays: null,
      reorderPoint: null,
      safetyStock: null,
      status: null,
      statusMessage: null,
    };
  }

  const safetyStock = Math.ceil(dailyDemand * safetyBufferDays);
  const reorderPoint = Math.ceil(dailyDemand * leadTimeDays + safetyStock);
  const coverageWeeksAtReorder =
    Math.round(
      (reorderPoint / dailyDemand / LEAD_TIME_ASSUMPTIONS.daysPerWeek) * 10,
    ) / 10;

  if (currentStock === null) {
    return {
      ...base,
      coverageWeeksAtReorder,
      daysUntilStockout: null,
      orderByDate: null,
      orderByDays: null,
      reorderPoint,
      safetyStock,
      status: null,
      statusMessage: null,
    };
  }

  const daysUntilStockout = Math.floor(currentStock / dailyDemand);
  const orderByDays = daysUntilStockout - planningHorizonDays;
  const orderByDate = toIsoDate(today, orderByDays);

  let status: LeadTimeStatus;
  let statusMessage: string;

  if (currentStock <= safetyStock) {
    status = 'critical';
    statusMessage = `Stock is at or below safety stock, with about ${daysUntilStockout} days of cover against a ${leadTimeDays}-day lead time. Order now and expect a gap.`;
  } else if (orderByDays < 0) {
    status = 'warning';
    statusMessage = `The order date passed about ${Math.abs(orderByDays)} days ago. Stock runs out in roughly ${daysUntilStockout} days but replenishment takes ${leadTimeDays} days.`;
  } else if (orderByDays === 0) {
    status = 'warning';
    statusMessage = `Today is the last safe day to order. Any later and the ${leadTimeDays}-day lead time eats into your safety buffer.`;
  } else {
    status = 'healthy';
    statusMessage = `Stock is healthy. You have about ${orderByDays} days before the order needs to be placed.`;
  }

  return {
    ...base,
    coverageWeeksAtReorder,
    daysUntilStockout,
    orderByDate,
    orderByDays,
    reorderPoint,
    safetyStock,
    status,
    statusMessage,
  };
}
